import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createCapEndpoint, selector as createCapSelector } from "./createCapSlice";
import { listCapEndpoints } from "../listCap/listCapSlice";
import { updateCapEndpoint, selector as updateCapSelector } from "../updateCapEndpointSlice";
import useHandleGroupSearch from "../../../deviceManagement/useHandleGroupSearch";
import {
  ISelectGroupState,
  IGroupAlerts,
} from "../../../../common/types/alertsTypes";
import { ITemplate } from "../../../../common/types/templateTypes";
import { getAllNodeValues } from "../../../../components/treeView/util";
import { getCapOrgId } from "../capUtils";
import useNotification from "../../../../components/notification/useNotification";
import { capWebhookURL } from "../../../../config";
import { Status } from "../../../../common/constants";
import { useSpinner } from "../../../../customHooks";
import {
  getOrgTemplates,
  getTemplates,
  mergeOrgTemplates,
  selector as templatesSelector,
} from "../../../../components/superAdmin/templatesSlice";
import { ICapEndpointRow } from "../capTypes";

const createSelectGroupState: ISelectGroupState = {
  groups: [],
  checked: [],
  selectAllChecked: false,
};

const buildInitialGroupState = (
  endpoint?: ICapEndpointRow | null,
): ISelectGroupState => {
  if (!endpoint?.groups?.length) {
    return createSelectGroupState;
  }
  const groups = endpoint.groups.map((group) => ({
    id: group.id || "",
    displayName: group.displayName || "",
  }));
  return {
    groups,
    checked: groups.map((group) => group.id).filter(Boolean),
    selectAllChecked: false,
  };
};

interface UseCreateCapOptions {
  editEndpoint?: ICapEndpointRow | null;
  onCancel: () => void;
}

const useCreateCap = ({ editEndpoint = null, onCancel }: UseCreateCapOptions) => {
  const dispatch: any = useDispatch();
  const { showSuccessNotification, showWarningNotification } =
    useNotification();
  const { handleGroupInputChange, groupSearchQuery, nodesToDisplay } =
    useHandleGroupSearch({ isEditable: false });
  const createCapState = useSelector(createCapSelector);
  const updateCapState = useSelector(updateCapSelector);
  const templatesState = useSelector(templatesSelector);
  const isEditMode = !!editEndpoint;

  const initialGroupState = buildInitialGroupState(editEndpoint);

  const [name, setName] = useState<string>(editEndpoint?.name || "");
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(
    editEndpoint?.templateId ?? null,
  );
  const [templateId, setTemplateId] = useState<number>(
    editEndpoint?.templateId || 0,
  );
  const [placedHolderGroupValues, setPlacedHolderGroupValues] =
    useState<string>(
      initialGroupState.groups.length > 0
        ? `(${initialGroupState.groups.length}) Groups Selected`
        : "Select Value",
    );
  const [selectGroupState, setSelectGroupState] = useState<ISelectGroupState>(
    initialGroupState,
  );
  const [selectedGroups, setSelectedGroups] = useState<IGroupAlerts[]>(
    initialGroupState.groups,
  );
  const [generatedWebhookUrl, setGeneratedWebhookUrl] = useState<string>("");
  const [showGeneratedModal, setShowGeneratedModal] = useState<boolean>(false);
  const [availableTemplates, setAvailableTemplates] = useState<ITemplate[]>([]);
  const [templateSearch, setTemplateSearch] = useState<string>("");

  const spinnerStatus =
    templatesState.status === Status.PENDING ||
    createCapState.status === Status.PENDING ||
    updateCapState.status === Status.PENDING
      ? Status.PENDING
      : Status.IDLE;
  useSpinner(spinnerStatus);

  const isSubmitting =
    createCapState.status === Status.PENDING ||
    updateCapState.status === Status.PENDING;

  const isTemplateDivSelected = (id: number) => selectedTemplate === id;

  const handleTemplateClick = (id: number) => {
    const newSelectedTemplate = selectedTemplate === id ? null : id;
    setSelectedTemplate(newSelectedTemplate);
    setTemplateId(newSelectedTemplate || 0);
  };

  const getAllValuesWithLabels = (nodes: any): IGroupAlerts[] => {
    let result: IGroupAlerts[] = [];
    function traverse(node: any) {
      result.push({
        id: node.value || node.id,
        displayName: node.label || node.displayName,
      });
      if (
        selectGroupState?.selectAllChecked &&
        node.children &&
        node.children.length > 0
      ) {
        for (const child of node.children) {
          traverse(child);
        }
      }
    }
    for (const node of nodes) {
      traverse(node);
    }
    return result;
  };

  const handleDataFromTreeView = (selectedGroup: any) => {
    const groups: IGroupAlerts[] = getAllValuesWithLabels(selectedGroup);
    setSelectedGroups(groups);
    const allNodeValues: string[] = getAllNodeValues(
      selectedGroup,
      selectGroupState.selectAllChecked,
    );
    setSelectGroupState({
      ...selectGroupState,
      groups,
      checked: allNodeValues,
    });
    setPlacedHolderGroupValues(
      groups.length > 0 ? `(${groups.length}) Groups Selected` : "Select Value",
    );
  };

  const resetForm = () => {
    setName("");
    setTemplateId(0);
    setSelectedTemplate(null);
    setSelectedGroups([]);
    setSelectGroupState(createSelectGroupState);
    setPlacedHolderGroupValues("Select Value");
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      showWarningNotification("Please enter a name for this CAP endpoint");
      return false;
    }
    if (selectedGroups.length === 0) {
      showWarningNotification("Please select at least one group");
      return false;
    }
    if (!templateId) {
      showWarningNotification("Please select a template");
      return false;
    }

    if (isEditMode && editEndpoint) {
      const response = await dispatch(
        updateCapEndpoint({
          endpointId: editEndpoint.id,
          name: name.trim(),
          groups: selectedGroups,
          templateId,
        }),
      );
      if (response?.meta?.requestStatus === "fulfilled") {
        showSuccessNotification("CAP endpoint updated successfully");
        dispatch(listCapEndpoints());
        onCancel();
        return true;
      }
      showWarningNotification(
        response?.payload?.response?.data?.message ||
          "Failed to update CAP endpoint",
      );
      return false;
    }

    const orgId = getCapOrgId();
    if (!orgId) {
      showWarningNotification("Organization not found");
      return false;
    }
    const response = await dispatch(
      createCapEndpoint({
        orgId,
        name: name.trim(),
        groups: selectedGroups,
        templateId,
      }),
    );
    if (response?.meta?.requestStatus === "fulfilled") {
      const createdEndpoint = response.payload?.message;
      setGeneratedWebhookUrl(
        `${capWebhookURL}${createdEndpoint?.endpointToken}`,
      );
      setShowGeneratedModal(true);
      showSuccessNotification("CAP endpoint created successfully");
      dispatch(listCapEndpoints());
      resetForm();
      return true;
    }
    showWarningNotification(
      response?.payload?.response?.data?.message ||
        "Failed to create CAP endpoint",
    );
    return false;
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      const orgId = getCapOrgId();
      if (!orgId) return;
      const [orgResult, publicResult] = await Promise.all([
        dispatch(getOrgTemplates(orgId)),
        dispatch(getTemplates({ public: true })),
      ]);
      if (getOrgTemplates.rejected.match(orgResult)) {
        showWarningNotification(
          orgResult.payload || "Failed to load organization templates",
        );
      }
      if (getTemplates.rejected.match(publicResult)) {
        showWarningNotification(
          publicResult.payload || "Failed to load public templates",
        );
      }
      const publicList = getTemplates.fulfilled.match(publicResult)
        ? publicResult.payload
        : [];
      const orgList = getOrgTemplates.fulfilled.match(orgResult)
        ? orgResult.payload
        : [];
      setAvailableTemplates(mergeOrgTemplates(publicList, orgList));
    };
    fetchTemplates();
  }, [dispatch]);

  const templates = useMemo(
    () =>
      availableTemplates.filter((template) =>
        (template.name || "")
          .toLowerCase()
          .includes((templateSearch || "").toLowerCase()),
      ),
    [availableTemplates, templateSearch],
  );

  return {
    name,
    setName,
    templateId,
    selectedGroups,
    isTemplateDivSelected,
    handleTemplateClick,
    handleGroupInputChange,
    groupSearchQuery,
    nodesToDisplay,
    handleDataFromTreeView,
    selectGroupState,
    setSelectGroupState,
    placedHolderGroupValues,
    handleSubmit,
    isSubmitting,
    generatedWebhookUrl,
    showGeneratedModal,
    setShowGeneratedModal,
    templates,
    templateSearch,
    setTemplateSearch,
    isEditMode,
  };
};

export default useCreateCap;
